import {
	createStore,
	createHook,
	createContainer,
	createActionsHook,
	defaultRegistry,
} from 'react-sweet-state';

import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

type NetworkStatus = {
	isCollabOffline: boolean;
};

export const initialNetworkStatus: NetworkStatus = {
	isCollabOffline: false,
};

export const actions = {
	setIsCollabOffline:
		(isCollabOffline: boolean) =>
		({ setState }: { setState: (state: Partial<NetworkStatus>) => void }): void => {
			setState({
				isCollabOffline,
			});
		},
};

type NetworkStatusContainerProps = {
	initialNetworkStatus?: Partial<NetworkStatus>;
};

export const networkStatusStore = createStore({
	initialState: {
		...initialNetworkStatus,
	},
	actions,
	name: 'networkStatus',
});

export const useCollabNetworkStatus = createHook(networkStatusStore, {
	selector: (state) => ({ isCollabOffline: state.isCollabOffline }),
});

export const useCollabNetworkStatusActions = createActionsHook(networkStatusStore);

export const NetworkStatusContainer = createContainer<
	NetworkStatus,
	typeof actions,
	NetworkStatusContainerProps
>(networkStatusStore, {
	displayName: 'NetworkStatusContainer',
	onInit:
		() =>
		({ setState }, { initialNetworkStatus }) => {
			if (initialNetworkStatus) {
				setState({
					...initialNetworkStatus,
				});
			}
		},
});

// Non-React utilities for accessing store outside of React components
const networkStatusStoreInstance = defaultRegistry.getStore(networkStatusStore);

/**
 * Get the current offline status outside of React components
 *
 * **Note:** This function is gated by the `platform_editor_offline_editing_web` experiment
 * with a kill switch at `platform_editor_offline_editing_wrapper_override`. If the feature
 * is not enabled, this will always return `false` (online).
 *
 * @returns true if offline, false if online
 */
export const getIsCollabOffline = (): boolean => {
	// eslint-disable-next-line confluence-feature-gating/no-preconditioning
	if (
		!expVal('platform_editor_offline_editing_web', 'isEnabled', false) ||
		fg('platform_editor_offline_editing_wrapper_override')
	) {
		return false;
	}
	return networkStatusStoreInstance.storeState.getState().isCollabOffline;
};

/**
 * Subscribe to network status changes outside of React components
 *
 * **Note:** This function is gated by the `platform_editor_offline_editing_web` experiment
 * with a kill switch at `platform_editor_offline_editing_wrapper_override`. If the feature
 * is not enabled, the listener will never be called.
 *
 * @param listener - Callback function to be called when network status changes
 * @returns Unsubscribe function
 */
export const subscribeToCollabNetworkStatus = (listener: () => void): (() => void) => {
	// eslint-disable-next-line confluence-feature-gating/no-preconditioning
	if (
		!expVal('platform_editor_offline_editing_web', 'isEnabled', false) ||
		fg('platform_editor_offline_editing_wrapper_override')
	) {
		return () => {};
	}
	return networkStatusStoreInstance.storeState.subscribe(listener);
};
